import * as Sentry from '@sentry/vue'
import type { Breadcrumb, ErrorEvent } from '@sentry/vue'
import type { App } from 'vue'

// Browser error reporting over the Sentry protocol, received by the self-hosted
// GlitchTip on the box (errors.vitesseterrain.com). One installer for every
// entrypoint; the `app` tag says which SPA an error came from.
export type ErrorTrackingApp = 'console' | 'handheld' | 'admin' | 'public_proposal'

// Path segments that ARE credentials: the customer's soumission and invoice links
// (/s/<token>, /f/<token>, and the API they call) and Active Storage's signed blob
// ids. Whoever reads the error must not be able to open the customer's link. The
// same list lives in config/initializers/sentry.rb — keep them in step.
const TOKEN_PATHS = /(\/(?:s|f|public_api\/jobs|rails\/active_storage\/(?:blobs|representations)(?:\/redirect|\/proxy)?|rails\/active_storage\/disk)\/)[^/?#\s"']+/g

export function redactTokens(text: string): string {
  return text.replace(TOKEN_PATHS, '$1[token]')
}

function redactBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  if (crumb.message) crumb.message = redactTokens(crumb.message)
  if (crumb.data) {
    for (const key of [ 'url', 'from', 'to' ]) {
      const value = crumb.data[key]
      if (typeof value === 'string') crumb.data[key] = redactTokens(value)
    }
  }
  return crumb
}

export function redactEvent(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) event.request.url = redactTokens(event.request.url)
  if (event.transaction) event.transaction = redactTokens(event.transaction)
  for (const exception of event.exception?.values ?? []) {
    if (exception.value) exception.value = redactTokens(exception.value)
  }
  event.breadcrumbs = event.breadcrumbs?.map(redactBreadcrumb)
  return event
}

// Must run BEFORE app.mount(): the SDK hooks Vue's errorHandler on the app it is
// given, and refuses an app that is already mounted. A production build without
// VITE_SENTRY_DSN reports nothing, and development never reports.
export function installErrorTracking(app: App, appName: ErrorTrackingApp) {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!import.meta.env.PROD || !dsn) return

  Sentry.init({
    app,
    dsn,
    environment: 'production',
    // No `release`: the only version the bundle knows is package.json's, which no
    // deploy changes, so it would label every build the same.
    initialScope: { tags: { app: appName } },
    // Errors only: no performance traces, no session replay.
    tracesSampleRate: 0,
    // What leaves the browser, pinned: SDK v11 collects all of this by default.
    // No user identity or IP, no cookies, headers, bodies or query strings, no
    // component props.
    attachProps: false,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    beforeSend: redactEvent,
    beforeBreadcrumb: redactBreadcrumb,
  })
}
